/**
 * components/team/teamStyles.js
 *
 * The style table, role colours and domain labels shared by every team screen
 * (organizer team, vendor team). Lifted from views/OrganizerTeam.js, which in
 * turn mirrors views/RBACManager.js, so all three read as one system.
 *
 * Accents follow the dashboard's action colour (#083860, the --lk-action token
 * in assets/css/lusuku-overrides.css). Dialogs, dropdowns and buttons are the
 * shared components/ui kit; the entries here only lay out the page around them.
 */

export const S = {
  page: { background: "#f8fafc", minHeight: "100vh", padding: "24px 20px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" },
  title: { fontSize: 26, fontWeight: 700, color: "#0f172a", margin: 0 },
  subtitle: { fontSize: 13, color: "#64748b", marginTop: 4 },
  tabBar: { display: "flex", gap: 4, background: "#fff", borderRadius: 10, padding: 4, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20, flexWrap: "wrap" },
  tab: (active) => ({ padding: "8px 16px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: active ? "#083860" : "transparent", color: active ? "#fff" : "#475569", transition: "all 0.15s" }),
  card: { background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: 20, marginBottom: 16 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14, marginBottom: 20 },
  kpi: (color) => ({ background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: `4px solid ${color}` }),
  kpiVal: { fontSize: 28, fontWeight: 800, color: "#0f172a" },
  kpiLabel: { fontSize: 12, color: "#64748b", marginTop: 2, fontWeight: 500 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 12 },
  badge: (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 4, background: bg, color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }),
  // The theme kills `.table-responsive` above 992px, so wide tables get their
  // own scroll container. Never swap this for <Table responsive>.
  tableWrap: { overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 880 },
  th: { textAlign: "left", padding: "10px 12px", background: "#f1f5f9", fontWeight: 600, color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" },
  td: { padding: "12px 12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "middle" },
  btnPrimary: { background: "#083860", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "#dc2626", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnGhost: { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnWarning: { background: "#b45309", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnSuccess: { background: "#047857", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  input: { border: "1px solid #cbd5e1", borderRadius: 8, height: 36, padding: "0 12px", fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" },
  select: { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none", boxSizing: "border-box" },
  label: { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block" },
  formRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 },
  modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 },
  modalBox: { background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 6 },
  modalNote: { fontSize: 13, color: "#64748b", marginBottom: 18, lineHeight: 1.5 },
  avatar: (bg) => ({ width: 36, height: 36, borderRadius: "50%", background: bg, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }),
  pill: (color, bg) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 700 }),
  alert: (type) => ({ padding: "12px 16px", borderRadius: 8, marginBottom: 14, fontSize: 13, background: type === "error" ? "#fef2f2" : type === "success" ? "#f0fdf4" : type === "warning" ? "#fffbeb" : "#eff6ff", color: type === "error" ? "#dc2626" : type === "success" ? "#047857" : type === "warning" ? "#b45309" : "#1d4ed8", border: `1px solid ${type === "error" ? "#fecaca" : type === "success" ? "#bbf7d0" : type === "warning" ? "#fde68a" : "#bfdbfe"}` }),
  matrixDomain: { marginBottom: 14 },
  matrixDomainTitle: { fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 },
  matrixGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 6 },
  matrixCell: (checked, disabled) => ({ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8, border: `1px solid ${checked ? "#b9cbe0" : "#e2e8f0"}`, background: checked ? "#eef4fa" : "#fff", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.8 : 1, fontSize: 12 }),
  spinner: { display: "inline-block", width: 18, height: 18, border: "2px solid #e2e8f0", borderTopColor: "#083860", borderRadius: "50%", animation: "spin 0.6s linear infinite" },
};

export const ROLE_META = {
  OWNER: { color: "#7c3aed", bg: "#ede9fe" },
  MANAGER: { color: "#1d4ed8", bg: "#dbeafe" },
  FINANCE: { color: "#065f46", bg: "#d1fae5" },
  BOX_OFFICE: { color: "#b45309", bg: "#fef3c7" },
  GATE: { color: "#0369a1", bg: "#e0f2fe" },
  ANALYST: { color: "#475569", bg: "#f1f5f9" },
  // Vendor team roles (docs/commerce/vendor-team-roles.md)
  ORDERS: { color: "#b45309", bg: "#fef3c7" },
  CATALOGUE: { color: "#0369a1", bg: "#e0f2fe" },
  PRICING: { color: "#065f46", bg: "#d1fae5" },
  VIEWER: { color: "#475569", bg: "#f1f5f9" },
};

export const DOMAIN_LABELS = {
  organizer: "Organizer",
  events: "Events",
  showings: "Showtimes",
  pricing: "Pricing",
  venues: "Venues",
  seatmaps: "Seat maps",
  holdPolicy: "Hold policy",
  tickets: "Tickets",
  refunds: "Refunds",
  boxOffice: "Box office",
  gate: "Gate",
  terminals: "Terminals",
  reports: "Reports",
  payouts: "Payouts",
  team: "Team",
  providers: "Integrations",
  audit: "Activity log",
  // Vendor team domains
  catalogue: "Catalogue",
  orders: "Orders",
  quotes: "Quotes",
  outlets: "Outlets and hours",
  settings: "Settings",
};
